import _ from 'underscore';
import { getDateRanges, RANGES } from '@bingads-webui/date-ranges';
import { DateRangePanelBase } from '../base/date-range-panel-base';

/**
 * The primary date range panel allows user to select the
 * main date range.
 * @param {Function} [dateRangesAccessor] - The optional function to get primary date ranges
 * @param {Function} [enabledRangesPredicate] - The optional function to filter primary date ranges
 * @param {Date} today - The date for today to be used in calculating date ranges
 * @param {Object} i18n - The i18n component. It should have method formatDate for formatting
 * dates and getString for getting localized strings
 * @extends {DateRangePanelBase}
 */
export class PrimaryDateRangePanel extends DateRangePanelBase {
  initialize(options) {
    super.initialize(options);
    this.showDateRanges = true;
    this.dateRangesAccessor = this.options.dateRangesAccessor || getDateRanges;
    this.enabledRangesPredicate = this.options.enabledRangesPredicate || this.getCommonDateRanges();
  }

  getCommonDateRanges() {
    const commonRanges = [RANGES.TODAY, RANGES.YESTERDAY, RANGES.THIS_WEEK_SUNDAY,
      RANGES.THIS_WEEK_MONDAY, RANGES.LAST_SEVEN_DAYS, RANGES.LAST_WEEK_SUNDAY,
      RANGES.LAST_WEEK_MONDAY, RANGES.LAST_FOURTEEN_DAYS, RANGES.LAST_THIRTY_DAYS,
      RANGES.THIS_MONTH, RANGES.LAST_MONTH, RANGES.LAST_THREE_MONTHS, RANGES.LAST_SIX_MONTHS,
      RANGES.THIS_YEAR, RANGES.LAST_YEAR, RANGES.ALL_TIME];
    return range => _.contains(commonRanges, range.key);
  }

  /**
   * Sets the state of the panels container
   * @param {Object} selection - The selected primary date range
   * @param {Date} selection.start - Primary range start date
   * @param {Date} selection.end - Primary range end date
   * @param {string} selection.key - Key for the primary range
   * @returns {PrimaryDateRangePanel} this
   */
  setState({ selection }) {
    if (selection.isCustom || !this.model.get('dateRanges')) {
      this.updateDateRanges(selection);
    }
    this.model.set({ selectedKey: selection.key });
    this.showDatePickersIfCustomSelected();
    return this;
  }

  /**
 * Update the date ranges that can be selected
 * @private
 * @param {any} [start] - The custom start end for the primary date range
 * @param {any} [end] - The custom end end for the primary date range
 * @returns {undefined}
 */
  updateDateRanges({ start, end }) {
    const { today, i18n } = this.options;

    const allDateRanges = this.dateRangesAccessor({
      today,
      i18n,
      customStart: start,
      customEnd: end,
    });

    const dateRanges = _.chain(allDateRanges)
      .mapObject(range => (this.enabledRangesPredicate(range) ? range : null))
      .omit(_.isNull)
      .value();
    this.model.set({ dateRanges });
  }
}
